import React from "react";
import {
  Backdrop,
  Box,
  Typography,
  CircularProgress,
  Button,
  Dialog,
  DialogContent,
  LinearProgress,
  Paper,
  Fade
} from "@mui/material";
import { useTheme } from "@mui/material/styles"; 
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import CloudSyncIcon from "@mui/icons-material/CloudSync";

const TemplateLoaderPopup = ({
  open,
  isLoading,
  title = "Fetching Fields",
  message = "Loading Fields...",
  progress = 0,
  stats,
  isValid,
  onSubmit,
  onViewResults,
  variant = "default" // <-- NEW PROP: "default" | "modern"
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark'; 

  const isAllPassed = stats?.failed === 0 && stats?.total > 0;
  const hasErrors = stats?.failed > 0;

  // ==========================================
  // UI VARIANT 2: THE NEW MODERN UI
  // ==========================================
  if (variant === "modern") {
    return (
      <Dialog
        open={Boolean(open)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            p: 1,
            boxShadow: isDark ? "0px 12px 32px rgba(0, 0, 0, 0.5)" : "0px 12px 32px rgba(0, 0, 0, 0.12)",
            bgcolor: 'background.paper', 
          }
        }}
      >
        <DialogContent sx={{ textAlign: "center", p: 4, overflow: "hidden" }}>
          {/* --- MODERN LOADING STATE --- */}
          {isLoading && (
            <Fade in={isLoading} timeout={400}>
              <Box>
                <Box
                  sx={{
                    display: "inline-flex",
                    p: 2,
                    borderRadius: "50%",
                    bgcolor: isDark ? 'rgba(144, 202, 249, 0.12)' : "primary.50",
                    mb: 3,
                    position: "relative"
                  }}
                >
                  <CircularProgress
                    size={64}
                    thickness={4}
                    sx={{ color: "primary.main", position: "absolute", top: 0, left: 0, p: 0.5 }}
                  />
                  <CloudSyncIcon sx={{ fontSize: 40, color: "primary.main" }} />
                </Box>

                <Typography variant="h6" fontWeight="600" gutterBottom color="text.primary">
                  {title}
                </Typography>
                <Typography variant="body2" color="text.secondary" mb={4}>
                  {message}
                </Typography>

                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                  <Typography variant="caption" color="text.secondary" fontWeight="600">
                    Validation Progress
                  </Typography>
                  <Typography variant="caption" color="primary.main" fontWeight="700">
                    {progress}%
                  </Typography>
                </Box>
                <Box sx={{ width: "100%", position: "relative" }}>
                  <LinearProgress
                    variant="determinate"
                    value={progress || 0}
                    sx={{ height: 8, borderRadius: 4 }}
                  />
                </Box>
              </Box>
            </Fade>
          )}

          {/* --- MODERN COMPLETED STATE --- */}
          {!isLoading && stats && (
            <Fade in={!isLoading} timeout={600}>
              <Box>
                {hasErrors ? (
                  <ErrorOutlineIcon sx={{ fontSize: 72, color: "error.main", mb: 2 }} />
                ) : (
                  <CheckCircleOutlineIcon sx={{ fontSize: 72, color: "success.main", mb: 2 }} />
                )}

                <Typography variant="h6" fontWeight="600" gutterBottom color="text.primary">
                  {isAllPassed ? "Validation Complete" : "Action Required"}
                </Typography>
                <Typography variant="body2" color="text.secondary" mb={4}>
                  {stats?.total === 0
                    ? "No valid data found in the file."
                    : isAllPassed
                      ? "All rows validated successfully. Review and submit."
                      : "We found some issues in your rows. Please review them before proceeding."}
                </Typography>

                <Paper
                  elevation={0}
                  sx={{
                    bgcolor: isDark ? 'rgba(255, 255, 255, 0.05)' : "grey.50",
                    border: "1px solid",
                    borderColor: isDark ? 'divider' : "grey.200",
                    borderRadius: 2,
                    p: 2,
                    mb: 4,
                    display: "flex",
                    justifyContent: "space-around"
                  }}
                >
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block" fontWeight="600">Total</Typography>
                    <Typography variant="h6" fontWeight="bold" color="text.primary">{stats.total}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="success.main" display="block" fontWeight="600">Passed</Typography>
                    <Typography variant="h6" fontWeight="bold" color="success.main">{stats.passed}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="error.main" display="block" fontWeight="600">Failed</Typography>
                    <Typography variant="h6" fontWeight="bold" color="error.main">{stats.failed}</Typography>
                  </Box>
                </Paper>

                <Box sx={{ display: "flex", justifyContent: "center" }}>
                  {stats?.total === 0 ? (
                    <Button fullWidth size="large" variant="contained" onClick={onViewResults} sx={{ py: 1.5, borderRadius: 2, textTransform: "none", fontSize: "1.05rem" }}>
                      Re-Upload File
                    </Button>
                  ) : isAllPassed ? (
                    <Button fullWidth size="large" variant="contained" color="success" onClick={onViewResults} sx={{ py: 1.5, borderRadius: 2, textTransform: "none", fontSize: "1.05rem" }}>
                      Review Data
                    </Button>
                  ) : (
                    <Button fullWidth size="large" variant="contained" color="primary" onClick={onViewResults} sx={{ py: 1.5, borderRadius: 2, textTransform: "none", fontSize: "1.05rem" }}>
                      View Results
                    </Button>
                  )}
                </Box>
              </Box>
            </Fade>
          )}
        </DialogContent>
      </Dialog>
    );
  }

  // ==========================================
  // UI VARIANT 1: THE ORIGINAL BACKDROP UI
  // ==========================================
  return (
    <Backdrop
      open={Boolean(open)}
      sx={{
        color: "#fff",
        zIndex: (theme) => theme.zIndex.drawer + 999,
        backgroundColor: "rgba(15, 23, 42, 0.48)",
      }}
    >
      <Box
        sx={{
          width: "min(520px, calc(100vw - 32px))",
          backgroundColor: "background.paper",
          borderRadius: 3,
          boxShadow: isDark ? "0 8px 32px rgba(0,0,0,0.6)" : 8,
          px: 3,
          py: 3,
        }}
      >
        {/* HEADER */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "linear-gradient(135deg, #2563eb, #7c3aed)",
              color: "#fff",
              boxShadow: "0 12px 24px rgba(37,99,235,.24)",
            }}
          >
            {isLoading && <CircularProgress size={22} thickness={5} color="inherit" />}
          </Box>

          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ color: "text.primary", fontWeight: 700, fontSize: "1.05rem" }}>
              {title}
            </Typography>
            <Typography sx={{ color: "text.secondary", fontSize: "0.92rem", mt: 0.5 }}>
              {isLoading
                ? message
                : isAllPassed
                ? "All rows validated successfully. Review and submit."
                : "Validation Completed"}
            </Typography>
          </Box>
        </Box>

        {/* PROGRESS LABEL */}
        {isLoading && (
          <>
            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.75 }}>
              <Typography sx={{ color: "text.secondary", fontSize: "0.8rem", fontWeight: 600 }}>
                Validation Progress
              </Typography>
              <Typography sx={{ color: isDark ? "#90caf9" : "#4f46e5", fontSize: "0.8rem", fontWeight: 700 }}>
                {progress}%
              </Typography>
            </Box>
            <Box sx={{ 
                width: "100%", height: 10, borderRadius: 999, overflow: "hidden", 
                backgroundColor: isDark ? 'rgba(255, 255, 255, 0.1)' : "#e2e8f0" 
              }}
            >
              <Box
                sx={{
                  width: `${progress}%`,
                  height: "100%",
                  borderRadius: 999,
                  transition: "width .35s ease",
                  background: "linear-gradient(90deg, #2563eb 0%, #7c3aed 100%)",
                }}
              />
            </Box>
          </>
        )}

        {/* RESULT SECTION */}
        {!isLoading && stats && (
          <Box sx={{ mt: 2 }}>
            <Typography sx={{ fontSize: "0.9rem", fontWeight: 600, color: "text.primary", alignItems: "center" }}>
              Total: {stats.total} | Passed: <span style={{ color: theme.palette.success.main }}>{stats.passed}</span> | Failed: <span style={{ color: theme.palette.error.main }}>{stats.failed}</span>
            </Typography>

            <Box sx={{ mt: 2, display: "flex", gap: 2, justifyContent: "center", alignItems: "center" }}>
              {stats?.total === 0 ? (
                <Button variant="contained" onClick={onViewResults}>
                  Re-Upload File
                </Button>
              ) : isAllPassed ? (
                <Button variant="contained" color="success" onClick={onViewResults}>
                  Review Data
                </Button>
              ) : (
                <Button variant="contained" onClick={onViewResults}>
                  View Results
                </Button>
              )}
            </Box>
          </Box>
        )}
      </Box>
    </Backdrop>
  );
};

export default TemplateLoaderPopup;